<script>
  import {
    Popup,
    Page,
    Navbar,
    NavRight,
    Link,
    Badge,
    Button,
    Icon,
    f7,
  } from "framework7-svelte";
  import { BASE_URL } from "../services/service.config";
  import { formatPrice, formatFullTimestamp as formatTimestamp } from "../js/audit-utils";

  export let show = false;
  export let signalId = null;
  export let environment = "LIVE";
  export let onClose = () => {};
  export let onInspectOrder = (orderTag) => {};

  let loading = false;
  let fanoutData = null;
  let error = null;

  $: if (show && signalId) {
    loadFanoutData(signalId, environment);
  }

  async function loadFanoutData(sigId, env) {
    loading = true;
    error = null;
    fanoutData = null;
    try {
      const res = await fetch(`${BASE_URL}/api/v1/audit/signals/${encodeURIComponent(sigId)}/fanout?environment=${env}`);
      if (!res.ok) {
        throw new Error(`Failed to load signal fanout (HTTP ${res.status})`);
      }
      const data = await res.json();
      fanoutData = data;
    } catch (err) {
      console.error("Error loading signal fanout:", err);
      error = err.message || "Failed to fetch signal fan-out";
    } finally {
      loading = false;
    }
  }
</script>

<Popup opened={show} onPopupClosed={onClose} class="audit-fanout-popup">
  <Page style="background: #0f172a; color: #e2e8f0;">
    <Navbar title="Strategy Signal Fan-Out Batch Inspector">
      <NavRight>
        <Link popupClose iconF7="xmark_circle_fill" />
      </NavRight>
    </Navbar>

  <div class="fanout-container">
    {#if loading}
      <div class="loading-box">
        <div class="spinner"></div>
        <p>Analyzing multi-client allocation fan-out for <code>{signalId}</code>...</p>
      </div>
    {:else if error}
      <div class="error-box">
        <strong>⚠️ Error:</strong> {error}
        <Button small fill color="blue" class="margin-top" onClick={() => loadFanoutData(signalId, environment)}>
          Retry
        </Button>
      </div>
    {:else if fanoutData}
      <!-- Signal Overview Header -->
      <div class="signal-header-card">
        <div class="sig-title-row">
          <div class="sig-name">
            <span class="strategy-badge">{fanoutData.signal?.strategy_id || 'STRATEGY'}</span>
            <strong>{fanoutData.signal?.symbol || 'SCRIP'}</strong>
            <span class="signal-id-code"><code>{fanoutData.signal?.signal_id || signalId}</code></span>
          </div>
          <div class="sig-time">
            ⏱ {formatTimestamp(fanoutData.signal?.timestamp || fanoutData.signal?.created_at)}
          </div>
        </div>

        {#if fanoutData.signal?.trigger_reason}
          <div class="sig-reason-box">
            <span class="r-label">Strategy Signal Trigger:</span>
            <span class="r-val">{fanoutData.signal.trigger_reason}</span>
          </div>
        {/if}

        <!-- Aggregate Summary Metrics Strip -->
        <div class="summary-metrics-strip">
          <div class="stat-card">
            <span class="stat-lbl">Subscribed Accounts</span>
            <span class="stat-num text-cyan">{fanoutData.summary?.total_subscribers || 0}</span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">RMS Approved</span>
            <span class="stat-num text-green">{fanoutData.summary?.rms_approved_count || 0}</span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">RMS Blocked</span>
            <span class="stat-num {fanoutData.summary?.rms_rejected_count > 0 ? 'text-red' : 'text-slate'}">
              {fanoutData.summary?.rms_rejected_count || 0}
            </span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">Orders Dispatched</span>
            <span class="stat-num text-blue">{fanoutData.summary?.orders_dispatched_count || 0}</span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">Orders Filled</span>
            <span class="stat-num text-green">{fanoutData.summary?.orders_filled_count || 0}</span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">Total Traded Qty</span>
            <span class="stat-num">{fanoutData.summary?.total_fill_qty || 0}</span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">Execution VWAP</span>
            <span class="stat-num text-purple">
              {formatPrice(fanoutData.summary?.vwap_fill_price)}
            </span>
          </div>
          <div class="stat-card">
            <span class="stat-lbl">Avg Slippage</span>
            <span class="stat-num {Number(fanoutData.summary?.avg_slippage_paise || 0) > 0 ? 'text-orange' : 'text-green'}">
              {fanoutData.summary?.avg_slippage_paise ? `${Number(fanoutData.summary.avg_slippage_paise).toFixed(1)} ps` : '0 ps'}
            </span>
          </div>
        </div>
      </div>

      <!-- Fan-Out Allocation Breakdown Table -->
      <div class="table-container">
        <div class="table-header-title">
          <span>👥 Subscribed Client Allocations & Execution Flow</span>
          <span class="sub-count">Showing {fanoutData.allocations?.length || 0} Accounts</span>
        </div>

        <table class="fanout-table">
          <thead>
            <tr>
              <th>Client UCC</th>
              <th>Multiplier</th>
              <th>RMS Status</th>
              <th>Order Tag</th>
              <th>Leg</th>
              <th>Req Qty</th>
              <th>Filled Qty</th>
              <th>Fill Price</th>
              <th>Slippage</th>
              <th>Timeline</th>
            </tr>
          </thead>
          <tbody>
            {#if !fanoutData.allocations || fanoutData.allocations.length === 0}
              <tr>
                <td colspan="10" class="empty-cell">No client allocations recorded for this signal.</td>
              </tr>
            {:else}
              {#each fanoutData.allocations as alloc}
                <tr class="{alloc.rms_status === 'REJECTED' ? 'row-rejected' : 'row-approved'}">
                  <td>
                    <span class="ucc-code">{alloc.client_ucc}</span>
                  </td>
                  <td>
                    <span class="mult-badge">{alloc.multiplier || 1.0}x</span>
                  </td>
                  <td>
                    {#if alloc.rms_status === 'APPROVED' || alloc.rms_status === 'PASSED'}
                      <span class="badge-pill bg-green">PASSED</span>
                    {:else if alloc.rms_status === 'REJECTED'}
                      <span class="badge-pill bg-red" title={alloc.reject_reason || 'RMS Gating'}>
                        BLOCKED: {alloc.reject_reason || 'RMS'}
                      </span>
                    {:else}
                      <span class="badge-pill bg-slate">{alloc.rms_status || 'PENDING'}</span>
                    {/if}
                  </td>
                  <td>
                    {#if alloc.order_tag}
                      <code class="order-code">{alloc.order_tag}</code>
                    {:else}
                      <span class="text-slate">-</span>
                    {/if}
                  </td>
                  <td>
                    <span class="leg-pill">{alloc.leg_index !== undefined ? `L${alloc.leg_index}` : 'L0'}</span>
                  </td>
                  <td>{alloc.order_qty || '-'}</td>
                  <td class="font-bold {alloc.fill_qty > 0 ? 'text-green' : ''}">
                    {alloc.fill_qty || (alloc.rms_status === 'REJECTED' ? '0' : '-')}
                  </td>
                  <td>
                    {formatPrice(alloc.fill_price)}
                  </td>
                  <td>
                    {#if alloc.slippage_paise !== undefined && alloc.slippage_paise !== null}
                      <span class="{alloc.slippage_paise > 0 ? 'text-orange' : 'text-green'}">
                        {alloc.slippage_paise} ps
                      </span>
                    {:else}
                      -
                    {/if}
                  </td>
                  <td>
                    {#if alloc.order_tag}
                      <Button small outline color="blue" onClick={() => onInspectOrder(alloc.order_tag)}>
                        Trace
                      </Button>
                    {:else}
                      -
                    {/if}
                  </td>
                </tr>
              {/each}
            {/if}
          </tbody>
        </table>
      </div>
    {/if}
  </div>
  </Page>
</Popup>

<style>
  .audit-fanout-popup {
    --f7-popup-tablet-width: 960px;
    --f7-popup-tablet-height: 88vh;
  }
  .fanout-container {
    padding: 16px;
    background: #0f172a;
    color: #e2e8f0;
    min-height: calc(100vh - 60px);
    font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
  }
  .loading-box {
    text-align: center;
    padding: 48px;
    color: #94a3b8;
  }
  .spinner {
    width: 32px;
    height: 32px;
    border: 3px solid #334155;
    border-top-color: #38bdf8;
    border-radius: 50%;
    animation: spin 0.8s linear infinite;
    margin: 0 auto 16px;
  }
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
  .error-box {
    background: #7f1d1d;
    color: #fecaca;
    padding: 16px;
    border-radius: 8px;
    margin: 16px 0;
  }

  .signal-header-card {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    padding: 14px 18px;
    margin-bottom: 16px;
  }
  .sig-title-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;
  }
  .sig-name {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
  }
  .strategy-badge {
    background: #581c87;
    color: #c084fc;
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 4px;
  }
  .signal-id-code code {
    background: #0f172a;
    color: #38bdf8;
    padding: 2px 6px;
    border-radius: 4px;
    border: 1px solid #334155;
    font-size: 12px;
  }
  .sig-time {
    font-size: 12px;
    color: #94a3b8;
  }
  .sig-reason-box {
    background: #0f172a;
    border-left: 3px solid #38bdf8;
    padding: 8px 12px;
    border-radius: 0 4px 4px 0;
    margin-bottom: 12px;
    font-size: 12px;
  }
  .r-label {
    color: #64748b;
    text-transform: uppercase;
    font-size: 10px;
    display: block;
  }
  .r-val {
    color: #f1f5f9;
  }

  .summary-metrics-strip {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(105px, 1fr));
    gap: 8px;
    margin-top: 10px;
  }
  .stat-card {
    background: #0f172a;
    border: 1px solid #334155;
    border-radius: 6px;
    padding: 8px 10px;
    display: flex;
    flex-direction: column;
  }
  .stat-lbl {
    font-size: 9px;
    color: #64748b;
    text-transform: uppercase;
    margin-bottom: 4px;
  }
  .stat-num {
    font-size: 15px;
    font-weight: bold;
    color: #f8fafc;
  }

  .table-container {
    background: #1e293b;
    border: 1px solid #334155;
    border-radius: 8px;
    overflow: hidden;
  }
  .table-header-title {
    padding: 12px 16px;
    background: #0f172a;
    border-bottom: 1px solid #334155;
    font-size: 13px;
    font-weight: 600;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }
  .sub-count {
    font-size: 11px;
    color: #94a3b8;
  }

  .fanout-table {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
  }
  .fanout-table th {
    background: #1e293b;
    color: #94a3b8;
    text-align: left;
    padding: 10px 12px;
    font-weight: 600;
    font-size: 11px;
    text-transform: uppercase;
    border-bottom: 1px solid #334155;
  }
  .fanout-table td {
    padding: 8px 12px;
    border-bottom: 1px solid #1e293b;
  }
  .row-rejected {
    background: rgba(127, 29, 29, 0.15);
  }
  .row-approved:hover {
    background: #273549;
  }

  .ucc-code {
    font-weight: 600;
    color: #38bdf8;
  }
  .mult-badge {
    background: #334155;
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 11px;
  }
  .badge-pill {
    padding: 2px 6px;
    border-radius: 4px;
    font-size: 10px;
    font-weight: bold;
  }
  .bg-green { background: #065f46; color: #34d399; }
  .bg-red { background: #7f1d1d; color: #f87171; }
  .bg-slate { background: #334155; color: #94a3b8; }
  .order-code {
    background: #0f172a;
    color: #cbd5e1;
    padding: 2px 4px;
    border-radius: 4px;
    font-size: 11px;
  }
  .leg-pill {
    background: #334155;
    color: #94a3b8;
    padding: 2px 4px;
    border-radius: 3px;
    font-size: 10px;
  }
  .empty-cell {
    text-align: center;
    padding: 32px;
    color: #64748b;
  }

  .text-green { color: #34d399 !important; }
  .text-red { color: #f87171 !important; }
  .text-blue { color: #38bdf8 !important; }
  .text-purple { color: #c084fc !important; }
  .text-orange { color: #fb923c !important; }
  .text-cyan { color: #22d3ee !important; }
  .text-slate { color: #94a3b8 !important; }
  .font-bold { font-weight: bold; }
</style>
